import type { Kitchen } from '@rta/rta-blueprint-library';
import { Document, Font, Page } from '@react-pdf/renderer';

import { fontConfig } from '../font-config';
import { lang } from '@lang';
import { styleSheet } from '../style-sheet';

import { BlueprintDocumentCover } from './blueprint-document-cover';
import { BlueprintDocumentKitchenOverview } from './blueprint-document-kitchen-overview';
import { BlueprintDocumentOverheadLayout } from './blueprint-document-overhead-layout';
import { BlueprintDocumentPageFooter } from './blueprint-document-page-footer';
import { BlueprintDocumentPageHeader } from './blueprint-document-page-header';
import { BlueprintDocumentPanelFooter } from './blueprint-document-panel-footer';
import { BlueprintDocumentPanelPage } from './blueprint-document-panel-page';
import { BlueprintDocumentRevisionLog } from './blueprint-document-revision-log';
import type { CustomerState, ProjectState } from '@state';

Font.register(fontConfig);

export type BlueprintDocumentProps = {
	data: {
		customer: CustomerState;
		project: ProjectState;
		kitchen: Kitchen | null | undefined;
		date: string;
		images: {
			[key: string]: string;
		};
		pages: Array<{
			pageType: string;
			pageNumber: number;
			title: string;
			islandId?: string;
			panelId?: string;
		}>;
	};
};

export const BlueprintDocument = (props: BlueprintDocumentProps): React.ReactElement | null => {
	const { customer, kitchen, project, date, images, pages } = props.data;

	if (!customer || !kitchen || !project) return null;

	const customerName = `${customer.firstName} ${customer.lastName}`;
	const designerName = `${project[0]?.designer?.firstName ?? ''} ${project[0]?.designer?.lastName ?? ''}`.trim() || lang.crm.text.designerUnassigned;
	const designerInitials = project[0]?.designer ? `${project[0].designer.firstName?.charAt(0) ?? ''}${project[0].designer.lastName?.charAt(0) ?? ''}` : '';
	const layoutDescription = `${kitchen.kitchenLayout} | ${Math.floor(kitchen.size.width / 12)}ft Grill Island`;
	const threeDImage = images.threed ?? '';

	const headerProps = {
		customerName,
		layoutDescription,
		designerName,
		creationDate: date,
		threeDImage
	};

	let pageCounter = 0;

	return (
		<Document>
			{pages.map((page) => {
				pageCounter++;

				switch (page.pageType) {
					case 'cover':
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName="" />
								<BlueprintDocumentCover threeDImage={threeDImage} />
								<BlueprintDocumentPageFooter page={pageCounter} />
							</Page>
						);

					case 'kitchen-overview':
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName="Kitchen Overview" />
								<BlueprintDocumentKitchenOverview kitchen={kitchen} />
								<BlueprintDocumentPageFooter page={pageCounter} />
							</Page>
						);

					case 'revision-log':
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName="Revision Log" />
								<BlueprintDocumentRevisionLog date={date} designerInitials={designerInitials} />
								<BlueprintDocumentPageFooter page={pageCounter} />
							</Page>
						);

					case 'overhead-layout':
					case 'base-layout':
					case 'countertop-layout':
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName={page.title} />
								<BlueprintDocumentOverheadLayout image={images.overhead ?? ''} />
								<BlueprintDocumentPageFooter page={pageCounter} />
							</Page>
						);

					case 'front-panel':
					case 'side-panels':
					case 'back-panel':
					case 'countertop':
					case 'combined-front-back': {
						const imageKey = page.panelId ?? `countertop-${page.islandId}`;
						const panelImage = images[imageKey] ?? '';
						const notes = page.pageType === 'front-panel'
							? ['Please Note: This view is the exterior side of the panel', 'MFG: Please add additional support to back panel']
							: page.pageType === 'side-panels'
								? ['Please Note: This view is the exterior/exposed side of the panels']
								: page.pageType === 'back-panel'
									? ['Please Note: This view is the exterior side of the back panel']
									: [];

						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName={page.title} />
								<BlueprintDocumentPanelPage image={panelImage} />
								<BlueprintDocumentPanelFooter notes={notes} page={pageCounter} />
							</Page>
						);
					}

					case 'joint-countertop':
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName={page.title} />
								<BlueprintDocumentOverheadLayout image={images.overhead ?? ''} />
								<BlueprintDocumentPanelFooter notes={['Please Note: Countertop seam joins where indicated']} page={pageCounter} />
							</Page>
						);

					case 'trim-pieces':
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName={page.title} />
								<BlueprintDocumentPanelPage image={''} />
								<BlueprintDocumentPanelFooter notes={['Please Note: See trim piece specifications below']} page={pageCounter} />
							</Page>
						);

					case 'corner-cad-mockup':
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName={page.title} />
								<BlueprintDocumentPanelPage image={''} />
								<BlueprintDocumentPageFooter page={pageCounter} />
							</Page>
						);

					default:
						return (
							<Page key={page.pageNumber} size="LETTER" orientation="landscape" style={styleSheet.proposalPage}>
								<BlueprintDocumentPageHeader {...headerProps} pageName={page.title} />
								<BlueprintDocumentPageFooter page={pageCounter} />
							</Page>
						);
				}
			})}
		</Document>
	);
};
